import React from 'react';

function BudgetsDashboard({ budgetsData, selectedYear, onEditBudget }) {
  const { budget = {}, summary = {} } = budgetsData;

  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 
    'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-teal-500'
  ];
  
  const bgColors = [
    'bg-blue-50', 'bg-green-50', 'bg-yellow-50', 'bg-red-50', 
    'bg-indigo-50', 'bg-purple-50', 'bg-pink-50', 'bg-teal-50'
  ];

  const borderColors = [
    'border-blue-500', 'border-green-500', 'border-yellow-500', 'border-red-500', 
    'border-indigo-500', 'border-purple-500', 'border-pink-500', 'border-teal-500'
  ];

  const textColors = [
    'text-blue-700', 'text-green-700', 'text-yellow-700', 'text-red-700', 
    'text-indigo-700', 'text-purple-700', 'text-pink-700', 'text-teal-700'
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const getMonthName = (monthNum) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[parseInt(monthNum) - 1] || monthNum;
  };

  const getTotalBudgetForYear = (year) => {
    const yearSummary = summary[year] || {};
    return Object.values(yearSummary).reduce((total, gl) => total + (gl.amount || 0), 0);
  };

  const getGLCodeStats = () => {
    const yearSummary = summary[selectedYear] || {};
    return Object.entries(yearSummary).map(([glCode, data]) => ({
      glCode,
      description: data.description,
      amount: data.amount,
      months: data.month?.length || 0
    })).sort((a, b) => b.amount - a.amount);
  };

  const getMonthlyBreakdown = () => {
    const monthlyData = {};
    const yearBudget = budget;
    
    Object.values(yearBudget).forEach(glData => {
      const yearData = glData.year?.[selectedYear] || {};
      Object.entries(yearData).forEach(([month, monthData]) => {
        if (!monthlyData[month]) {
          monthlyData[month] = { total: 0, count: 0 };
        }
        monthlyData[month].total += monthData.amount || 0;
        monthlyData[month].count += 1;
      });
    });

    return Object.entries(monthlyData)
      .map(([month, data]) => ({
        month: parseInt(month),
        monthName: getMonthName(month),
        total: data.total,
        count: data.count
      }))
      .sort((a, b) => a.month - b.month);
  };

  // New helper for All Years view (Side-by-Side)
  const renderYearSummaryCard = (year, index) => {
    const yearSummary = summary[year] || {};
    const totalAmount = Object.values(yearSummary).reduce((sum, item) => sum + (item.amount || 0), 0);
    const headerColor = colors[index % colors.length];

    return (
      <div key={year} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
        <div className={`${headerColor} px-4 py-3`}>
          <h3 className="text-lg font-semibold text-white text-center">{year}</h3>
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GL Code</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(yearSummary).map(([glCode, data]) => (
                <tr key={glCode}>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{glCode}</td>
                  <td className="px-3 py-2 text-sm text-gray-500">{data.description}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(data.amount)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900"></td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">Total</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(totalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!selectedYear) {
    // All Years View (Side-by-Side)
    const years = Object.keys(summary).sort((a, b) => b - a); // Sort descending
    
    if (years.length === 0) {
       return <div className="text-center py-8 text-gray-500">No budget data available.</div>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {years.map((year, index) => renderYearSummaryCard(year, index))}
      </div>
    );
  }

  const totalBudget = getTotalBudgetForYear(selectedYear);
  const glCodeStats = getGLCodeStats();
  const monthlyBreakdown = getMonthlyBreakdown();

  return (
    <div className="space-y-6">
      {/* GL Code Breakdown */}
      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Budget by GL Code</h3>
        </div>
        <div className="p-6">
          {glCodeStats.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No budget data available for {selectedYear}</p>
          ) : (
            <div className="space-y-4">
              {glCodeStats.map((glStat, index) => (
                <div key={glStat.glCode} className={`flex items-center justify-between p-4 rounded-lg border-l-4 ${borderColors[index % borderColors.length]} ${bgColors[index % bgColors.length]} shadow-sm`}>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`font-medium ${textColors[index % textColors.length]}`}>{glStat.glCode}</h4>
                        <p className="text-sm text-gray-600">{glStat.description}</p>
                        <p className="text-xs text-gray-500">{glStat.months} months planned</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">{formatCurrency(glStat.amount)}</p>
                        <p className="text-sm text-gray-500">
                          {((glStat.amount / totalBudget) * 100).toFixed(1)}% of total
                        </p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-white rounded-full h-2 bg-opacity-50">
                        <div 
                          className={`h-2 rounded-full ${colors[index % colors.length]}`} 
                          style={{ width: `${(glStat.amount / totalBudget) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Monthly Budget Breakdown</h3>
        </div>
        <div className="p-6">
          {monthlyBreakdown.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No monthly budget data available for {selectedYear}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {monthlyBreakdown.map((monthData, index) => (
                <div key={monthData.month} className={`p-4 rounded-lg hover:shadow-md transition-shadow ${bgColors[index % bgColors.length]}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className={`font-medium ${textColors[index % textColors.length]}`}>{monthData.monthName}</h4>
                      <p className="text-sm text-gray-600">{monthData.count} GL codes</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900">{formatCurrency(monthData.total)}</p>
                      <p className="text-sm text-gray-500">
                        {((monthData.total / totalBudget) * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Budget Items */}
      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Recent Budget Items</h3>
          <button
            onClick={() => onEditBudget?.(null)}
            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
          >
            View All
          </button>
        </div>
        <div className="p-6">
          {Object.keys(budget).length === 0 ? (
            <p className="text-gray-500 text-center py-8">No budget items available</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(budget).slice(0, 5).map(([glCode, glData]) => {
                const yearData = glData.year?.[selectedYear] || {};
                const latestMonth = Object.keys(yearData).sort((a, b) => b - a)[0];
                const monthData = yearData[latestMonth] || {};
                
                return (
                  <div key={glCode} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 px-2 rounded transition-colors">
                    <div>
                      <h4 className="font-medium text-gray-900">{glCode}</h4>
                      <p className="text-sm text-gray-600">{glData.description}</p>
                      <p className="text-xs text-gray-500">
                        {latestMonth ? `${getMonthName(latestMonth)} ${selectedYear}` : 'No recent data'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(monthData.amount)}</p>
                      <button
                        onClick={() => onEditBudget?.(monthData.budget_id)}
                        className="text-blue-600 hover:text-blue-900 text-sm"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BudgetsDashboard;
